import { clsx } from "clsx";
import type { MouseEvent, ReactElement, ReactNode } from "react";
import { PureComponent, createRef } from "react";
import Measure, { type ContentRect } from "react-measure";
import type { ConnectedComponent } from "react-redux";

import {
  ActionButton,
  ActionButtonIntent,
  type ActionButtonProps,
  ActionButtonSize,
  AlertDialog,
  HeaderBar,
  HeaderDivider,
  SymbolIcon,
  TriggerButton,
  TriggerButtonIntent,
} from "widgets";

import style from "./styles/GenericCard.module.scss";

import type { IGenericCardConfiguration } from "../../redux/modules/dashboard.js";
import type { IGenericCardEditingProps } from "./GenericCardEditingDialog.js";
import { ControlledPopover, TooltipController } from "./overlays/components/OverlayControllers.js";

export interface IGenericCardProps {
  id: string;
  title: string;
  editMode: boolean;
  cardPanelClassName?: string;
  isFullscreen?: boolean;
  toggleFullscreen?: (id: string) => void;
  refreshData?: () => Promise<any>;
  goToAction?: () => void;

  titleElement?: ReactElement;
  mainAction?: ActionButtonProps;
  removeCard: (cardId: string) => void;
  saveCardConfig: (cardConfiguration: IGenericCardConfiguration) => void;
  config: IGenericCardConfiguration;
  editingForm?: ConnectedComponent<any, IGenericCardEditingProps>;
  titleTrailer?: string;
  scrollMoreCallback?: (scrollMore: boolean) => void;
  isDraggable?: boolean;
  footerContent?: any;
  tipTourId?: string;
  isMovingCard?: boolean;
  children: ReactNode;
}

interface IGenericCardState {
  isFetchingData: boolean;
  showDeleteCardPopover: boolean;
  showEditingForm: boolean;
  scrollMore: boolean;
}

/**
 * @deprecated
 */
class GenericCard extends PureComponent<IGenericCardProps, IGenericCardState> {
  private deleteCardRef = createRef<HTMLButtonElement>();
  private contentRef = createRef<HTMLDivElement>();
  private measure: (() => void) | null = null; // measure function from Measure in order to force the measure.

  state = {
    isFetchingData: false,
    showDeleteCardPopover: false,
    showEditingForm: false,
    scrollMore: false,
  };

  componentDidUpdate(prevProps: Readonly<IGenericCardProps>): void {
    if (this.props.config.specificConfig !== prevProps.config.specificConfig) {
      this.refreshData();
    }

    this.measure?.();
  }

  checkNeedExpand(rect: ContentRect) {
    if (!rect) return;

    const scrollMore = (rect.scroll?.height ?? 0) - 10 > (rect.offset?.height ?? 0);

    // need to force scroll to zero or the top of the list will be hidden if you scroll on fullScreen mode
    if (!this.props.isFullscreen && this.contentRef.current) {
      this.contentRef.current.scrollTop = 0;
    }

    this.setState({ scrollMore });
    this.props.scrollMoreCallback?.(scrollMore);
  }

  refreshData = () => {
    this.setState({ isFetchingData: true });
    this.props
      .refreshData?.()
      .then(() => {
        this.setState({ isFetchingData: false });
      })
      .catch(() => {
        this.setState({ isFetchingData: false });
      });
  };

  toggleFullscreen = () => {
    return this.props.toggleFullscreen?.(this.props.id);
  };

  render(): ReactElement {
    const CardEditingComponent: ConnectedComponent<any, IGenericCardEditingProps> | undefined = this.props.editingForm;
    return (
      <div
        className={clsx("card-panel", {
          fullscreen: this.props.isFullscreen,
          [this.props.cardPanelClassName ?? ""]: !!this.props.cardPanelClassName,
        })}
        data-testid={"dashboard-card"}
        data-e2e-edit={String(this.props.editMode)}
      >
        <HeaderBar
          title={this.props.title}
          subtitle={this.props.titleTrailer}
          className={style.header_bar}
          tipTourId={!this.props.editMode ? this.props.tipTourId : undefined}
          actions={[
            this.props.titleElement,
            this.props.mainAction ? (
              <ActionButton
                intent={ActionButtonIntent.Secondary}
                tooltip={this.props.mainAction.label}
                onPress={this.props.mainAction.onPress}
                size={ActionButtonSize.Small}
              >
                {this.props.mainAction.children}
              </ActionButton>
            ) : null,
            !!this.props.toggleFullscreen && !this.props.isFullscreen && this.state.scrollMore && (
              <TriggerButton
                label={"Expand to See More"}
                onPress={() => {
                  this.toggleFullscreen();
                }}
                icon={"open_in_full"}
              />
            ),
            !!this.props.toggleFullscreen && this.props.isFullscreen && (
              <TriggerButton
                icon={"close_fullscreen"}
                label={"Exit Full Screen Mode"}
                onPress={this.toggleFullscreen}
              />
            ),
            this.props.refreshData ? (
              <TriggerButton icon={"sync"} label={"Refresh"} onPress={this.refreshData} />
            ) : null,
            this.props.goToAction ? (
              <>
                <HeaderDivider />
                <TriggerButton icon={"read_more"} label={"See Additional Content"} onPress={this.props.goToAction} />
              </>
            ) : null,
          ]}
        />
        <Measure offset scroll onResize={this.checkNeedExpand.bind(this)}>
          {({ measureRef, measure }) => {
            this.measure = measure;
            return (
              <div
                className={clsx("panel-content", { "has-bottom": this.props.footerContent != undefined })}
                ref={measureRef}
                data-testid={"dashboard-card-content"}
              >
                <div
                  ref={(ref) => {
                    this.contentRef.current = ref;
                  }}
                >
                  {this.props.children}
                </div>
              </div>
            );
          }}
        </Measure>
        {this.props.footerContent != undefined && <div className="panel-bottom">{this.props.footerContent}</div>}
        {this.props.editMode && (
          <div className={"card-edit-mode-overlay"}>
            <div className={"card-edit-mode-toolbar"} data-testid={"dashboard-card-edit-toolbar"}>
              <TooltipController
                title="Drag handle"
                content={"Change the layout of your dashboard by positioning this card with drag'n drop"}
                overlayOptions={{ isDisabled: this.props.isMovingCard }}
              >
                {(tooltipTriggerProps) => (
                  <div
                    // we cannot inject all the tooltip trigger props because of the dragging behavior
                    onPointerEnter={tooltipTriggerProps.onPointerEnter}
                    onPointerLeave={tooltipTriggerProps.onPointerLeave}
                    ref={tooltipTriggerProps.ref}
                    className={clsx("action-button", "drag-handle-action", {
                      disabled: this.props.isDraggable === false,
                    })}
                    role="button"
                  >
                    <SymbolIcon icon={"drag_indicator"} />
                  </div>
                )}
              </TooltipController>
              {CardEditingComponent && (
                <TriggerButton
                  label={"Configure Card"}
                  onPress={() => this.showEditCardDialog()}
                  icon={"settings"}
                  tooltip={
                    !this.props.isMovingCard
                      ? "Change the title and tweak the configuration specific to this card type"
                      : undefined
                  }
                />
              )}
              <TriggerButton
                label={"Delete Card"}
                tooltip={
                  !this.props.isMovingCard ? "The configuration specific to this card type will be lost" : undefined
                }
                onPress={() => this.showDeleteCardPopover()}
                icon={"delete"}
                isDisabled={this.props.isDraggable === false}
                intent={TriggerButtonIntent.Danger}
                ref={this.deleteCardRef}
              />
            </div>
          </div>
        )}
        {this.state.showDeleteCardPopover && (
          <ControlledPopover
            triggerRef={this.deleteCardRef}
            close={() => this.setState({ showDeleteCardPopover: false })}
          >
            {(controllerProps) => (
              <AlertDialog
                {...controllerProps}
                mainButton={{
                  label: "Delete",
                  onPress: () => this.props.removeCard(this.props.id),
                }}
              >
                Delete this card?
              </AlertDialog>
            )}
          </ControlledPopover>
        )}

        {this.state.showEditingForm && CardEditingComponent && (
          <CardEditingComponent
            saveCardConfig={this.props.saveCardConfig}
            initialConfig={this.props.config}
            onClose={this.closeEditCardDialog}
          />
        )}
      </div>
    );
  }

  showDeleteCardPopover = (e?: MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation(); // so that the drag behavior is prevented

    if (this.props.isDraggable) {
      this.setState({ showDeleteCardPopover: true });
    }
  };

  showEditCardDialog = (e?: MouseEvent) => {
    this.setState({ showEditingForm: true });
    e?.preventDefault();
    e?.stopPropagation(); // so that the drag behavior is prevented
  };

  closeEditCardDialog = () => {
    this.setState({ showEditingForm: false });
  };
}

export default GenericCard;
